import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowUpRight, Loader2 } from "lucide-react";
import { Seo } from "@/lib/seo";
import { SITE } from "@/lib/site";
import { Fade, Mask } from "@/components/Reveal";
import SectionLabel from "@/components/SectionLabel";

const FORM_ENDPOINT: string =
  (process.env.REACT_APP_FORM_ENDPOINT as string) ||
  `${process.env.REACT_APP_BACKEND_URL}/api/contact`;

const PROJECT_TYPES = [
  "Sonorisation",
  "Éclairage",
  "Régie technique",
  "Infrastructures scéniques",
  "Scénographie & fabrication",
  "Autre",
];

type FormState = {
  nom: string;
  societe: string;
  email: string;
  telephone: string;
  type_projet: string;
  date_evenement: string;
  lieu: string;
  description: string;
  rgpd: boolean;
};

const initial: FormState = {
  nom: "",
  societe: "",
  email: "",
  telephone: "",
  type_projet: "",
  date_evenement: "",
  lieu: "",
  description: "",
  rgpd: false,
};

const field =
  "w-full border-b border-line bg-transparent py-3 text-base text-ink placeholder:text-faint focus:border-ink focus:outline-none transition-colors duration-300";
const label = "font-mono text-[10px] uppercase tracking-[0.25em] text-mute";

export default function Contact() {
  const [form, setForm] = useState<FormState>(initial);
  const [sending, setSending] = useState(false);

  const set = (key: keyof FormState) => (v: string | boolean) =>
    setForm((f) => ({ ...f, [key]: v }));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      const res = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error(String(res.status));
      toast.success("Votre demande a bien été envoyée. Nous revenons vers vous rapidement.", {
        style: { background: "#171717", color: "#F5F5F2", border: "1px solid #333333" },
      });
      setForm(initial);
    } catch {
      toast.error("L'envoi a échoué. Contactez-nous directement : " + SITE.email, {
        style: { background: "#171717", color: "#F5F5F2", border: "1px solid #333333" },
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <main id="contenu">
      <Seo
        title="Contact — Construisons votre prochain projet | SONOCO EVENTS"
        description="Contactez SONOCO EVENTS, prestataire technique événementiel à Fréjus (Var) : sonorisation, éclairage, régie technique, structures scéniques et scénographie. Devis et étude technique sur mesure."
        path="/contact"
      />

      <header className="mx-auto max-w-[1600px] px-5 pb-14 pt-32 sm:px-10 sm:pb-20 sm:pt-44 lg:px-16">
        <SectionLabel num="05" name="Contact" />
        <h1 className="mt-10 max-w-5xl text-[clamp(2.6rem,8vw,8rem)] font-extrabold uppercase leading-[0.88] tracking-tightest">
          <Mask>Construisons</Mask>
          <Mask delay={0.1}>votre prochain projet.</Mask>
        </h1>
      </header>

      <section className="mx-auto max-w-[1600px] px-5 pb-28 sm:px-10 sm:pb-40 lg:px-16">
        <div className="grid gap-16 lg:grid-cols-12">
          <Fade className="lg:col-span-7">
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="grid gap-8 sm:grid-cols-2"
              noValidate={false}
            >
              <div className="sm:col-span-1">
                <label htmlFor="nom" className={label}>
                  Nom / Prénom *
                </label>
                <input
                  id="nom"
                  data-testid="contact-input-nom"
                  required
                  value={form.nom}
                  onChange={(e) => set("nom")(e.target.value)}
                  className={`${field} mt-2`}
                  autoComplete="name"
                />
              </div>
              <div className="sm:col-span-1">
                <label htmlFor="societe" className={label}>
                  Société
                </label>
                <input
                  id="societe"
                  data-testid="contact-input-societe"
                  value={form.societe}
                  onChange={(e) => set("societe")(e.target.value)}
                  className={`${field} mt-2`}
                  autoComplete="organization"
                />
              </div>
              <div>
                <label htmlFor="email" className={label}>
                  Adresse e-mail *
                </label>
                <input
                  id="email"
                  type="email"
                  data-testid="contact-input-email"
                  required
                  value={form.email}
                  onChange={(e) => set("email")(e.target.value)}
                  className={`${field} mt-2`}
                  autoComplete="email"
                />
              </div>
              <div>
                <label htmlFor="telephone" className={label}>
                  Téléphone
                </label>
                <input
                  id="telephone"
                  type="tel"
                  data-testid="contact-input-telephone"
                  value={form.telephone}
                  onChange={(e) => set("telephone")(e.target.value)}
                  className={`${field} mt-2`}
                  autoComplete="tel"
                />
              </div>
              <div>
                <label htmlFor="type_projet" className={label}>
                  Type de projet
                </label>
                <select
                  id="type_projet"
                  data-testid="contact-select-type-projet"
                  value={form.type_projet}
                  onChange={(e) => set("type_projet")(e.target.value)}
                  className={`${field} mt-2`}
                >
                  <option value="">Sélectionner…</option>
                  {PROJECT_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="date_evenement" className={label}>
                  Date de l'événement
                </label>
                <input
                  id="date_evenement"
                  type="date"
                  data-testid="contact-input-date-evenement"
                  value={form.date_evenement}
                  onChange={(e) => set("date_evenement")(e.target.value)}
                  className={`${field} mt-2`}
                />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="lieu" className={label}>
                  Lieu de l'événement
                </label>
                <input
                  id="lieu"
                  data-testid="contact-input-lieu"
                  value={form.lieu}
                  onChange={(e) => set("lieu")(e.target.value)}
                  className={`${field} mt-2`}
                  placeholder="Ville, département…"
                />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="description" className={label}>
                  Description du projet *
                </label>
                <textarea
                  id="description"
                  data-testid="contact-input-description"
                  required
                  rows={5}
                  value={form.description}
                  onChange={(e) => set("description")(e.target.value)}
                  className={`${field} mt-2 resize-none`}
                  placeholder="Contexte, lieu, formats envisagés, contraintes techniques…"
                />
              </div>

              <div className="sm:col-span-2">
                <label className="flex cursor-pointer items-start gap-3 text-sm leading-relaxed text-mute">
                  <input
                    type="checkbox"
                    data-testid="contact-rgpd-checkbox"
                    checked={form.rgpd}
                    required
                    onChange={(e) => set("rgpd")(e.target.checked)}
                    className="mt-1 h-4 w-4 shrink-0 appearance-none border border-line bg-transparent checked:bg-ink"
                  />
                  <span>
                    J'accepte que mes données soient utilisées pour traiter ma demande, conformément
                    à la{" "}
                    <Link
                      to="/mentions-legales#rgpd"
                      data-testid="contact-rgpd-link"
                      className="underline underline-offset-4 hover:text-ink"
                    >
                      politique de confidentialité
                    </Link>
                    . *
                  </span>
                </label>
              </div>

              <div className="sm:col-span-2">
                <button
                  type="submit"
                  data-testid="contact-form-submit-button"
                  disabled={sending}
                  className="group inline-flex items-center gap-3 border border-ink/30 px-8 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-ink transition-all duration-300 hover:border-ink hover:bg-ink hover:text-bg disabled:cursor-wait disabled:opacity-60"
                >
                  {sending ? "Envoi en cours…" : "Envoyer ma demande"}
                  {sending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  )}
                </button>
              </div>
            </form>
          </Fade>

          <Fade className="space-y-10 lg:col-span-4 lg:col-start-9" delay={0.15}>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-faint">E-mail</p>
              <a
                href={`mailto:${SITE.email}`}
                data-testid="contact-sidebar-email"
                className="link-sweep mt-3 inline-block text-lg text-ink"
              >
                {SITE.email}
              </a>
            </div>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-faint">
                Téléphone
              </p>
              {SITE.phones.map((p) => (
                <a
                  key={p}
                  href={`tel:${p.replace(/\s/g, "")}`}
                  data-testid="contact-sidebar-phone"
                  className="link-sweep mt-3 block text-lg text-ink"
                >
                  {p}
                </a>
              ))}
            </div>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-faint">Base</p>
              <p className="mt-3 text-lg text-ink">{SITE.location}</p>
              <p className="mt-2 text-sm leading-relaxed text-mute">
                Interventions dans le Sud de la France et en région PACA. Projets ailleurs : étudiés
                selon leurs exigences.
              </p>
            </div>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-faint">Suivre</p>
              <a
                href={SITE.instagram}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-sidebar-instagram"
                className="link-sweep mt-3 inline-flex items-center gap-1.5 text-lg text-ink"
              >
                Instagram <ArrowUpRight className="h-4 w-4" />
              </a>
            </div>
            <p className="border-t border-line pt-6 text-sm leading-relaxed text-mute" id="rgpd">
              Les informations transmises via ce formulaire sont utilisées uniquement pour traiter
              votre demande et ne sont jamais transmises à des tiers. Pour exercer vos droits
              d'accès, de rectification ou de suppression : {SITE.email}.
            </p>
          </Fade>
        </div>
      </section>
    </main>
  );
}
